/** How a policy or quote is named to the consumer: carrier and product. */
import { fundingPolicyTypeDefs } from '@shared';
import { consumerQuoteCarrier, type ConsumerQuote } from '@shared/consumerPlan';

interface StoredQuoteName {
  productName?: string;
  carrierName?: string;
  carrier?: string;
}

function storedQuote(policy: PolicyFundingSource): StoredQuoteName | null {
  return typeof policy.sourceQuote === 'string'
    ? safeParse(policy.sourceQuote)
    : (policy.sourceQuote ?? null);
}

export function policyCarrierName(policy: PolicyFundingSource): string | null {
  const quote = storedQuote(policy);
  // A policy added from a quote carries its carrier only inside the stored quote. Hybrid quotes keep
  // the display name in `carrierName` (`carrier` is a code); traditional and short-term ones in `carrier`.
  const carrier = policy.carrierName || quote?.carrierName || quote?.carrier;
  return typeof carrier === 'string' ? carrier : null;
}

export function policyDisplayName(policy: PolicyFundingSource): string {
  const quote = storedQuote(policy);
  const carrier = policyCarrierName(policy);
  const productName =
    quote?.productName ??
    (policy.policyType ? fundingPolicyTypeDefs[policy.policyType]?.label : null);
  return [carrier, productName].filter(Boolean).join(' · ') || 'Long-term care policy';
}

export function quoteDisplayName(quote: ConsumerQuote): string {
  return `${consumerQuoteCarrier(quote)} · ${quote.productName}`;
}

function safeParse(value: string): StoredQuoteName | null {
  try {
    return JSON.parse(value) as StoredQuoteName;
  } catch {
    return null;
  }
}
